<template>
  <div class="rounded-md border border-input/70 p-3">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0 flex-1">
        <div class="wrap-break-word text-sm font-medium">
          <Badge variant="outline" class="mr-2">
            v{{ formatVersion(faq.version) }}
          </Badge>{{ faq.question }}
        </div>
        <div class="relative mt-1">
          <div class="pointer-events-none absolute inset-x-0 top-0 -z-10 opacity-0">
            <p ref="answerMeasurement" class="wrap-break-word text-sm text-muted-foreground">
              {{ answerText }}
            </p>
          </div>

          <Transition name="faq-answer-slide" mode="out-in">
            <div v-if="isExpanded" key="expanded" class="overflow-hidden">
              <div class="wrap-break-word cursor-pointer text-sm text-muted-foreground transition-colors duration-200 hover:text-foreground/80" @click="toggleExpanded">
                {{ answerText }}
              </div>
            </div>
            <div v-else key="collapsed" class="overflow-hidden">
              <div class="wrap-break-word text-sm text-muted-foreground transition-colors duration-200" :class="isExpandable ? 'line-clamp-3 cursor-pointer hover:text-foreground/80' : ''" @click="toggleExpanded">
                {{ answerText }}
              </div>
            </div>
          </Transition>
        </div>
      </div>

      <FaqOptionsDropdown
        :is-deleting="isDeleting"
        :question="String(faq?.question ?? '')"
        @edit="startEditing"
        @delete="handleDelete"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'
import FaqOptionsDropdown from './FaqOptionsDropdown.vue'

const props = defineProps({
  faq: {
    type: Object,
    required: true,
  },
  isDeleting: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['edit', 'delete'])
const answerMeasurement = ref(null)
const isExpanded = ref(false)
const isExpandable = ref(false)

const answerText = computed(() => {
  const answer = props.faq?.answer_content_text

  if (typeof answer === 'string' && answer.trim() !== '') {
    return answer
  }

  return 'No answer has been provided yet.'
})

const formatVersion = (versionValue) => {
  if (versionValue === null || versionValue === undefined || versionValue === '') {
    return '1.00'
  }

  const normalizedVersion = Number(versionValue)

  if (Number.isNaN(normalizedVersion)) {
    return String(versionValue)
  }

  return normalizedVersion.toFixed(2)
}

const measureAnswer = async () => {
  await nextTick()

  const answerElement = answerMeasurement.value

  if (!(answerElement instanceof HTMLElement)) {
    return
  }

  const computedStyles = window.getComputedStyle(answerElement)
  const parsedLineHeight = Number.parseFloat(computedStyles.lineHeight)
  const normalizedLineHeight = Number.isFinite(parsedLineHeight)
    ? parsedLineHeight
    : Number.parseFloat(computedStyles.fontSize) * 1.5

  const nextCollapsedHeight = Math.ceil(normalizedLineHeight * 3)
  const nextExpandedHeight = Math.ceil(answerElement.scrollHeight)

  isExpandable.value = nextExpandedHeight > nextCollapsedHeight + 1

  if (!isExpandable.value) {
    isExpanded.value = false
  }
}

const toggleExpanded = () => {
  if (!isExpandable.value) {
    return
  }

  isExpanded.value = !isExpanded.value
}

const startEditing = () => {
  emit('edit', props.faq)
}

const handleDelete = () => {
  emit('delete', props.faq)
}

watch(answerText, () => {
  isExpanded.value = false
  measureAnswer()
}, { immediate: true })

onMounted(() => {
  measureAnswer()
  window.addEventListener('resize', measureAnswer)
})

onUnmounted(() => {
  window.removeEventListener('resize', measureAnswer)
})
</script>

<style scoped>
.faq-answer-slide-enter-active,
.faq-answer-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.faq-answer-slide-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.faq-answer-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
